"use client";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { NewsletterCheckbox } from "@/features/account/NewsletterCheckbox";
import { useTranslations } from "@/i18n/client";
import type { StoryblokRichtext } from "@/storyblok-base";
import { useSubscribe } from "@frend-digital/centra/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import styles from "./EmailSubscription.module.css";

const useSuccessMessage = (timeout?: number) => {
	const [successMessage, setSuccessMessage] = useState<string>("");

	useEffect(() => {
		if (successMessage && timeout) {
			const timer = setTimeout(() => {
				setSuccessMessage("");
			}, timeout);

			return () => clearTimeout(timer);
		}
	}, [successMessage, timeout]);

	return { successMessage, setSuccessMessage };
};

interface EmailSubscriptionFormProps {
	terms?: StoryblokRichtext;
	preview?: boolean;
}

export const EmailSubscriptionForm = ({ terms, preview }: EmailSubscriptionFormProps) => {
	const t = useTranslations();

	const newsletterSignupSchema = z
		.object({
			email: z.string().email({ message: t("Please enter your email address") }),
			newsletter: z.boolean(),
		})
		.superRefine((data, ctx) => {
			if (data.email && !data.newsletter) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("You must accept the terms to subscribe"),
					path: ["newsletter"],
				});
			}
		});

	type NewsletterInput = z.infer<typeof newsletterSignupSchema>;

	const subscribe = useSubscribe();
	const { successMessage, setSuccessMessage } = useSuccessMessage(5000);

	const methods = useForm<NewsletterInput>({
		resolver: zodResolver(newsletterSignupSchema),
		defaultValues: {
			email: "",
			newsletter: false,
		},
	});

	const {
		register,
		handleSubmit,
		setError,
		reset,
		formState: { errors },
	} = methods;

	const onSubmit: SubmitHandler<NewsletterInput> = async ({ email }) => {
		subscribe.mutate(
			{
				email,
			},
			{
				onSuccess: () => {
					setSuccessMessage(
						t("Thank you for subscribing! Check your inbox for a confirmation email."),
					);
					reset();
				},
				onError: () => {
					setError("email", {
						message: t("Something went wrong"),
					});
				},
			},
		);
	};

	return (
		<FormProvider {...methods}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.inputWrapper}>
					<label htmlFor="email-subscription" className={styles.label}>
						{t("Your email")}
					</label>
					<div className={styles.inputRow}>
						<Input
							id="email-subscription"
							placeholder={t("Email")}
							className={styles.input}
							{...register("email", { required: true })}
						/>
					</div>
				</div>
				{errors.email && <p className={styles.error}>{errors.email?.message}</p>}

				{terms && (
					<div className={styles.checkboxWrapper}>
						<NewsletterCheckbox
							signupTerms={terms}
							signupLabel=""
							showError={false}
							termsClassName={styles.termsNoMargin}
						/>
					</div>
				)}
				{errors.newsletter && <p className={styles.error}>{errors.newsletter?.message}</p>}

				<Button type="submit" loading={subscribe.isPending} className={styles.submitButton}>
					{t("Subscribe")}
				</Button>

				{successMessage && <p className={styles.success}>{successMessage}</p>}
			</form>
		</FormProvider>
	);
};
